"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { myFeedbackEq, FeatureAnnouncementsUserRpc } from "./api/feedback-conversations";
import { MY_FEEDBACK_UNREAD_COUNT_QUERY_KEY } from "@/entities/feature-announcement-feedback";
import { applyReactionToggle, type ReactionThreadCache } from "../lib/apply-reaction-toggle";

// ============================================================================
// Query Key Factory — shared by BOTH user surfaces (the inbox page and the
// widget "Your feedback" tab), so a mutation in one refreshes the other within
// the same session. (Does NOT cross the user<->admin boundary — separate keys.)
// ============================================================================

export const MY_FEEDBACK_QUERY_KEYS = {
  all: ["my-feedback"] as const,
  list: ["my-feedback", "list"] as const,
  thread: (feedbackId: string) => ["my-feedback", "thread", feedbackId] as const,
  // Lives in the entity (the badge hook polls it there); re-exposed here so all
  // user-surface keys stay visible in one factory.
  unreadCount: MY_FEEDBACK_UNREAD_COUNT_QUERY_KEY
};

// ============================================================================
// Queries
// ============================================================================

export function useMyFeedback() {
  const {
    data,
    isPending,
    isFetching,
    error
  } = useQuery({
    ...myFeedbackEq.queryOptions({
      queryKey: MY_FEEDBACK_QUERY_KEYS.list,
      queryFn: () => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.listMyFeedback();
      }).pipe(Effect.scoped)
    }),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000
  });
  return {
    items: data?.items ?? [],
    isLoading: isPending && isFetching,
    error
  };
}
export function useMyFeedbackThread(feedbackId: string | null) {
  const {
    data,
    isPending,
    isFetching,
    error
  } = useQuery({
    ...myFeedbackEq.queryOptions({
      queryKey: MY_FEEDBACK_QUERY_KEYS.thread(feedbackId ?? "none"),
      queryFn: () => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.getMyFeedbackThread({
          feedbackId: feedbackId!
        });
      }).pipe(Effect.scoped)
    }),
    enabled: !!feedbackId,
    staleTime: 15 * 1000
  });
  return {
    feedback: data?.feedback ?? null,
    messages: data?.messages ?? [],
    isLoading: isPending && isFetching,
    error
  };
}

// ============================================================================
// Mutations
// ============================================================================

export interface ReplyAttachmentPayload {
  readonly screenshotId: string;
  readonly s3Key: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly contentType: string;
}
export function useReplyToFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    ...myFeedbackEq.mutationOptions({
      mutationFn: (input: {
        feedbackId: string;
        content: string;
        attachments?: ReadonlyArray<ReplyAttachmentPayload>;
      }) => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.replyToFeedback({
          feedbackId: input.feedbackId,
          content: input.content,
          attachments: input.attachments ?? []
        });
      }).pipe(Effect.scoped)
    }),
    onSuccess: (_data, variables) => {
      // Refresh the open thread (new message) and the list (awaiting-reply facet).
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.thread(variables.feedbackId)
      });
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.list
      });
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.unreadCount
      });
    }
  });
}

/**
 * Presign step for the user-side reply composer: pairs with the entity's
 * useReplyAttachments (prepare → client PUT; no completeScreenshotUpload — the
 * screenshot row is created atomically with the note by replyToFeedback).
 */
export function usePrepareReplyScreenshot() {
  const mutation = useMutation({
    ...myFeedbackEq.mutationOptions({
      mutationFn: (input: {
        feedbackId: string;
        fileName: string;
        fileSize: number;
        contentType: string;
      }) => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.prepareScreenshotUpload(input);
      }).pipe(Effect.scoped)
    })
  });
  return mutation.mutateAsync;
}

/**
 * Flip an emoji on one bubble of the caller's own thread.
 *
 * Optimistic with rollback: the chip must respond to the click instantly, and a
 * failed toggle must not leave a phantom reaction behind.
 *
 * Invalidates the thread AND the list — `awaitingReply` is derived server-side
 * and must not desync. Deliberately NOT `unreadCount`: the unread dot derives
 * from submitter_last_viewed_at, which reactions never touch.
 */
export function useToggleFeedbackReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    ...myFeedbackEq.mutationOptions({
      mutationFn: (input: {
        feedbackId: string;
        noteId: string | null;
        emoji: string;
      }) => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.toggleMyFeedbackReaction(input);
      }).pipe(Effect.scoped)
    }),
    onMutate: async variables => {
      const key = MY_FEEDBACK_QUERY_KEYS.thread(variables.feedbackId);
      // Cancel first, or an in-flight refetch can land after the flip and undo it.
      await queryClient.cancelQueries({
        queryKey: key
      });
      const previous = queryClient.getQueryData<ReactionThreadCache>(key);
      if (previous) {
        queryClient.setQueryData(key, applyReactionToggle(previous, variables.noteId, variables.emoji));
      }
      return {
        previous,
        key
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSettled: (_data, _error_0, variables_0) => {
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.thread(variables_0.feedbackId)
      });
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.list
      });
    }
  });
}
export function useMarkFeedbackViewed() {
  const queryClient = useQueryClient();
  return useMutation({
    ...myFeedbackEq.mutationOptions({
      mutationFn: (input: {
        feedbackId: string;
      }) => Effect.gen(function* () {
        const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
        return yield* client.markFeedbackViewed(input);
      }).pipe(Effect.scoped)
    }),
    onSuccess: (_data, variables) => {
      // The unread dot is derived from submitter_last_viewed_at — without this it
      // never clears. Refresh the list, the open thread, and the button badge.
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.list
      });
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.thread(variables.feedbackId)
      });
      queryClient.invalidateQueries({
        queryKey: MY_FEEDBACK_QUERY_KEYS.unreadCount
      });
    }
  });
}